import { useState, useMemo, useCallback } from 'react';
import { View, StyleSheet, Modal as RNModal } from 'react-native';
import { Button, Reminder, theme, Text, styles as baseStyles } from '@socrait/react';

import { useViewAllRemindersFeature } from '~/featureFlags';
import { ActionItem } from '~/services/types';
import { useActionItemUpdateStatusMutation } from '~/services/review';
import { ViewAllRemindersScreen } from '~/screens/viewAllReminders/ViewAllRemindersScreen';

type RemindersProps = {
  remindersData: ActionItem[];
};

export const sortReminders = (reminders: ActionItem[]) => {
  const newReminders = reminders
    .filter((reminder) => reminder.status === 'new')
    .sort((a, b) => {
      return a.title.localeCompare(b.title);
    });
  const completedReminders = reminders
    .filter((reminder) => reminder.status === 'completed')
    .sort((a, b) => {
      return a.title.localeCompare(b.title);
    });

  return [...newReminders, ...completedReminders];
};

export const Reminders = ({ remindersData }: RemindersProps) => {
  const [loadingIDs, setLoadingIDs] = useState<string[]>([]);
  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);

  const isViewAllRemindersEnabled = useViewAllRemindersFeature();
  const { mutate: updateActionItemStatus } = useActionItemUpdateStatusMutation();

  const onUpdateActionItemStatus = useCallback(
    (reminder: ActionItem) => {
      setLoadingIDs((prev) => [...prev, reminder.id]);
      updateActionItemStatus(
        {
          actionItemId: reminder.id,
          status: reminder.status === 'new' ? 'completed' : 'new',
        },
        {
          onSuccess: () => {
            setLoadingIDs((prev) => prev.filter((id) => id !== reminder.id));
          },
          onError: () => {
            setLoadingIDs((prev) => prev.filter((id) => id !== reminder.id));
          },
        },
      );
    },
    [updateActionItemStatus],
  );

  const noRemindersContent = useMemo(() => {
    return (
      <View style={styles.noRemindersContainer}>
        <Text style={styles.noRemindersText}>No reminders found</Text>
      </View>
    );
  }, []);

  const reminderContent = useMemo(() => {
    if (remindersData.length === 0) {
      return noRemindersContent;
    }
    return (
      <View style={styles.remindersContainer}>
        {remindersData.map((reminder) => (
          <Reminder
            key={reminder.id}
            details={reminder.title}
            status={reminder.status}
            loading={loadingIDs.includes(reminder.id)}
            onPress={() => onUpdateActionItemStatus(reminder)}
          />
        ))}
      </View>
    );
  }, [remindersData, loadingIDs, noRemindersContent, onUpdateActionItemStatus]);

  return (
    <View style={styles.container}>
      <View style={styles.remindersTitleContainer}>
        <Text style={styles.title}>Reminders</Text>
        {isViewAllRemindersEnabled && (
          <Button
            name="View All"
            onPress={() => setIsViewAllModalOpen(true)}
            containerStyle={styles.viewAllButton}
            fontStyle={styles.viewAllButtonText}
          />
        )}
      </View>
      <View style={styles.remindersContainer}>{reminderContent}</View>
      {isViewAllRemindersEnabled && (
        <RNModal
          visible={isViewAllModalOpen}
          onRequestClose={() => setIsViewAllModalOpen(false)}
          animationType="none"
          transparent={true}>
          <ViewAllRemindersScreen onClose={() => setIsViewAllModalOpen(false)} />
        </RNModal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    gap: theme.spacing.medium,
  },
  remindersTitleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  title: {
    ...baseStyles.title,
    paddingHorizontal: theme.spacing.medium,
    paddingVertical: theme.spacing.xsmall,
    fontSize: theme.fontSizes.small,
  },
  remindersContainer: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.medium,
    width: 300,
  },
  noRemindersContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.gray,
    borderRadius: theme.borderRadius.large,
    display: 'flex',
    height: 60,
    justifyContent: 'center',
    padding: theme.spacing.medium,
    width: 300,
  },
  noRemindersText: {
    color: theme.colors.darkGray,
    textAlign: 'center',
    fontSize: theme.fontSizes.small,
  },
  viewAllButton: {
    backgroundColor: theme.colors.white,
  },
  viewAllButtonText: {
    fontSize: theme.fontSizes.small,
  },
});
